import { BeforeRequestMiddleware, Context } from '../types';

/** HTTP methods considered safe, not needing CSRF protection. */
const CSRF_SAFE_METHODS = ['GET', 'HEAD', 'OPTIONS', 'TRACE'];

const CSRFTOKEN_COOKIE_NAME = 'csrftoken';

/** Cached token as a promise to avoid cache stampede */
let csrfTokenPromise: Promise<string> | null = null;

/**
 * Return `true` if `method` is considered as a safe CSRF HTTP method.
 */
function isSafeMethod(method?: string): boolean {
  return CSRF_SAFE_METHODS.includes(method || '');
}

/**
 * Return `true` if both `url1` and `url2` have the same origin.
 */
function isSameOrigin(url1: string, url2: string) {
  try {
    return new URL(url1).origin === new URL(url2).origin;
  } catch (err) {
    return false;
  }
}

/**
 * Return `true` if the current request needs to pass a CSRF token.
 */
function needsCsrfToken(method: string, context: Context): boolean {
  // We currently don't support CSRF for non-browser environments.
  if (!context.isBrowser) return false;
  // Safe methods don't need the CSRF header.
  if (isSafeMethod(method)) return false;
  // Cross-origin requests external to the `baseURL` don't need a CSRF token.
  if (
    !isSameOrigin(context.url, context.origin) &&
    !isSameOrigin(context.url, context.baseUrl)
  ) {
    return false;
  }
  return true;
}

/**
 * Fetch the CSRF token from the monolith's CRSF endpoint.
 */
async function fetchCsrfToken(context: Context): Promise<string> {
  const url = `${context.baseUrl || context.origin}/api/v3/csrf`;
  const res = await context.fetcher.call(null, url, {
    credentials: 'include',
  });
  return res.headers.get('x-upstreamcsrftoken') as string;
}

/**
 * Return the CSRF token for the current request.
 *
 * It first tries to read it from the cached resolved token promise. We use a
 * promise here to avoid a cache stampede if multiple requests are made in
 * parallel while fetching the token.
 *
 * If the cache fails, it tries to read the token from the cookie, which should
 * be available for same-origin requests. If the cookie is not present or has
 * expired, it fetches a new CSRF token.
 */
async function negociateCsrfToken(context: Context): Promise<string> {
  if (!csrfTokenPromise) {
    csrfTokenPromise = Promise.resolve(
      getCookieByName(CSRFTOKEN_COOKIE_NAME) || fetchCsrfToken(context)
    );
  }
  return await csrfTokenPromise;
}

export function clearCsrfCache() {
  csrfTokenPromise = null;
}

/**
 * retrieve the value of a named cookie,
 * or null if the cookie is not found
 *
 * @param name the name of the cookie
 */
function getCookieByName(name: string): string | null {
  const cookieString = document.cookie;
  const cookies = cookieString.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith(name + '=')) {
      return cookie.substring(name.length + 1);
    }
  }
  return null;
}

/**
 * Set the `x-csrftoken` header for requests that need a CSRF token.
 */
export const csrf: BeforeRequestMiddleware = async (init, context) => {
  if (needsCsrfToken(init.method, context)) {
    const csrfToken = await negociateCsrfToken(context);
    clearCsrfCache();
    init.headers.set('x-csrftoken', csrfToken);
  }
  return init;
};
